import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LoginForm from './components/LoginForm';
import CompanyBranding from './components/CompanyBranding';

const SecureAuthenticationPortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionTimeout, setSessionTimeout] = useState(null);

  // Credenciales con detección automática de rol
  const mockCredentials = {
    'Mateo': { password: 'pechugas67', role: 'administrator' },
    'Isaac': { password: 'pechugas67', role: 'technician' },
    'Jose': { password: 'pechugas67', role: 'technician' },
    'Jesus': { password: 'pechugas67', role: 'director' },

    // 🔹 Usuario de pruebas → va directo a /work-orders
    'Paco': { password: 'SistemasAgs', role: 'technician' }
  };


  // ✅ FUNCIÓN DE REDIRECCIÓN
  const redirectToDashboard = (role) => {
    switch (role) {
      case 'administrator':
        navigate('/dashboard');
        break;
      case 'technician':
        navigate('/work-orders');
        break;
      case 'director':
        navigate('/financial');
        break;
      default:
        navigate('/dashboard');
    }
  };

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('authToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    const userRole = localStorage.getItem('userRole');

    if (token && tokenExpiry && userRole) {
      const now = new Date().getTime();
      const expiry = parseInt(tokenExpiry);

      if (now < expiry) {
        redirectToDashboard(userRole);
      } else {
        clearAuthenticationData();
      }
    }

    // Session timeout warning
    const timeoutWarning = setTimeout(() => {
      setSessionTimeout('Su sesión expirará en 5 minutos. Por favor, guarde su trabajo.');
    }, 25 * 60 * 1000);

    return () => clearTimeout(timeoutWarning);
  }, []);

  const clearAuthenticationData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('rememberDevice');
  };

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Enhanced credential validation with automatic role detection
      const username = formData.username.trim();
      const userCredentials = mockCredentials[username];

      if (!userCredentials || userCredentials.password !== formData.password) {
        throw new Error('Credenciales inválidas. Verifique su usuario y contraseña.');
      }

      // Auto-detect role from username
      const userRole = userCredentials.role;

      // Generate secure token
      const token = `jwt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const expiry = new Date().getTime() + (30 * 60 * 1000);

      // Store authentication data
      localStorage.setItem('authToken', token);
      localStorage.setItem('tokenExpiry', expiry.toString());
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('username', username);

      if (formData.rememberDevice) {
        localStorage.setItem('rememberDevice', 'true');
      }

      // Redirect with animation
      setTimeout(() => {
        redirectToDashboard(userRole);
      }, 500);

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      const form = document.querySelector('form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4"
      onKeyDown={handleKeyDown}
    >
      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Column - Company Branding */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden lg:block"
        >
          <CompanyBranding />
        </motion.div>

        {/* Right Column - Login Form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card border border-border rounded-lg shadow-sm p-8"
        >
          <div className="text-center mb-8">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-2xl font-semibold text-card-foreground mb-2"
            >
              Iniciar Sesión
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-muted-foreground"
            >
              Ingrese sus credenciales para continuar
            </motion.p>
          </div>

          {/* Session Timeout Warning */}
          <AnimatePresence>
            {sessionTimeout && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="mb-6 p-3 bg-warning/10 border border-warning/20 rounded-lg"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-warning rounded-full animate-pulse"></div>
                  <span className="text-sm text-warning">{sessionTimeout}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <LoginForm
            onLogin={handleLogin}
            isLoading={isLoading}
            error={error}
          />
        </motion.div>
      </div>

      {/* Mobile Company Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="lg:hidden fixed bottom-4 left-4 right-4"
      >
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">EP</span>
            </div>
            <span className="font-semibold text-card-foreground">ElektronikPro</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Sistema de Gestión Empresarial
          </p>
        </div>
      </motion.div>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-card border border-border rounded-lg p-6 text-center"
            >
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-card-foreground font-medium">Verificando credenciales</p>
              <p className="text-muted-foreground text-sm mt-1">Por favor espere</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecureAuthenticationPortal;